import React, { Component } from 'react';
import {
    Button,
    Card,
    Col,
    Form,
    message,
    Row,
    Select,
    Skeleton,
    Spin,
    Table,
    DatePicker,
    Empty
} from "antd";
import moment from "moment";
import { connect } from "react-redux";
import { saveAs } from 'file-saver';
import json2csv from 'json2csv';
import axios from 'util/Api'
import { fetchBowserAssets } from "../../../appRedux/actions/Asset";
import { getBowserFuelTransactions, downloadBowserFuelTransactions, deleteBowserFuelTransaction } from "../../../appRedux/actions/Fuel";
import './style.css';

const { RangePicker } = DatePicker;

class BowserTransactionReport extends Component {

    state = {
        currentTimePeriod: 'daily',
        currentPage: 1,
        pageSize: 10,
        downloadLoading: false
    };

    getDayBoundaries = (fromInput, toInput) => {
        return {
            periodFrom: moment(fromInput).startOf('day').valueOf(),
            periodTo: moment(toInput).endOf('day').valueOf(),
        };
    };

    componentDidMount() {
        // Set initial time period from form field
        const initialTimePeriod = this.props.form.getFieldValue('timePeriod') || 'daily';
        this.setState({ currentTimePeriod: initialTimePeriod });

        // Load bowser assets on component mount
        this.props.fetchBowserAssets();

        // Check for URL parameters and auto-populate form
        this.checkAndPopulateFromURL();
    }

    checkAndPopulateFromURL = () => {
        // Get route parameters from props or route match
        const { match, location } = this.props;

        // Extract parameters from the route path
        const pathSegments = location.pathname.split('/');
        // console.log(pathSegments)

        // Based on the path structure: ["", "fuel", "bowser-transaction-report", "bowserId", "fromTimestamp", "toTimestamp"]
        if (pathSegments.length >= 6) {
            const bowserParam = pathSegments[3];
            const fromDateParam = pathSegments[4];
            const toDateParam = pathSegments[5];

            if (bowserParam && fromDateParam && toDateParam &&
                bowserParam !== 'undefined' && fromDateParam !== 'undefined' && toDateParam !== 'undefined') {

                // Convert timestamps to moment objects
                const fromDate = parseInt(fromDateParam);
                const toDate = parseInt(toDateParam);

                if (fromDate && toDate) {
                    const normalizedPeriod = this.getDayBoundaries(fromDate, toDate);
                    this.props.form.setFieldsValue({
                        bowserAsset: bowserParam,
                        periodFromTo: [moment(normalizedPeriod.periodFrom), moment(normalizedPeriod.periodTo)]
                    });

                    // Auto-generate the report after a short delay to ensure bowser assets are loaded
                    setTimeout(() => {
                        this.autoGenerateReport(bowserParam, normalizedPeriod.periodFrom, normalizedPeriod.periodTo);
                    }, 500);
                }
            }
        }
    };

    autoGenerateReport = (bowserAsset, fromDate, toDate) => {
        const payload = {
            bowserAsset: bowserAsset,
            periodFrom: fromDate,
            periodTo: toDate,
        };
        this.generateBowserTransactionReport(payload);
    };

    handleTimePeriodChange = (value) => {
        this.setState({ currentTimePeriod: value });
    };

    handleSubmit = e => {
        e.preventDefault();
        e.stopPropagation();
        this.props.form.validateFields((err, values) => {
            if (err) {
                // Form validation failed
            } else {
                if (moment(values.periodFromTo[0]).isSameOrBefore(values.periodFromTo[1])) {
                    const normalizedPeriod = this.getDayBoundaries(values.periodFromTo[0], values.periodFromTo[1]);
                    const payload = {
                        bowserAsset: values.bowserAsset,
                        periodFrom: normalizedPeriod.periodFrom,
                        periodTo: normalizedPeriod.periodTo,
                    };
                    this.generateBowserTransactionReport(payload);
                } else {
                    message.error("Period From couldn't come earlier than Period To!");
                }
            }
        });
    };

    generateBowserTransactionReport = async (payload, resetPagination = true) => {
        try {
            // Call the bowser-fuel-transactions API
            await this.props.getBowserFuelTransactions(
                {
                    ...payload,
                    offset: (this.state.currentPage - 1) * this.state.pageSize,
                    limit: this.state.pageSize,
                });

            // Reset pagination only when generating new report (not on pagination changes)
            if (resetPagination) {
                this.setState({
                    currentPage: 1
                });
            }
        } catch (error) {
            message.error('Failed to generate report. Please try again.');
            console.error('Error generating bowser transaction report:', error);
        }
    };

    downloadReport = async () => {
        try {
            this.setState({ downloadLoading: true });

            const { getFieldValue } = this.props.form;
            const values = {
                bowser: getFieldValue('bowserAsset'),
                periodFrom: moment(getFieldValue('periodFromTo')[0]).startOf('day').valueOf(),
                periodTo: moment(getFieldValue('periodFromTo')[1]).endOf('day').valueOf(),
                // timePeriod: getFieldValue('timePeriod') || 'daily'
            };

            // Call download API
            const response = await axios.get('/bowser-fuel-transactions/download', {
                params: values
            });

            if (response && response.data) {
                // Convert data to CSV format
                const fields = [
                    { label: 'Date', value: (row) => row.datetime && moment(row.datetime).format('DD-MM-YYYY HH:mm:ss') },
                    { label: 'Bowser', value: (row) => row.bowser && row.bowser.assetCode },
                    // { label: 'Location', value: (row) => row.bowser && row.bowser.location && row.bowser.location.name },
                    { label: 'Transaction Type', value: 'bowserRefuelingType' },
                    { label: 'Quantity Added', value: 'quantityAdded' },
                    { label: 'Quantity Removed', value: 'quantityRemoved' },
                    { label: 'Refueled Asset', value: (row) => row.refAsset && row.refAsset.assetCode },
                    { label: 'Balance', value: 'balance' },
                    // { label: 'Fuel Type', value: (row) => row.bowser && row.bowser.assetModel && row.bowser.assetModel.fuelType && row.bowser.assetModel.fuelType.name },
                    { label: 'Refuel Voucher', value: (row) => row.refFuelRecord && row.refFuelRecord.voucherNo },
                    // { label: 'User', value: (row) => row.user && row.user.name }
                ];

                const csv = json2csv.parse(response.data, { fields });

                // Create a Blob from the CSV data
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

                // Save the CSV file using FileSaver.js
                const timePeriod = this.state.currentTimePeriod;
                saveAs(blob, `bowser-transaction-report-${timePeriod}-${new Date().getTime()}.csv`);

                message.success('Report downloaded successfully!');
            }
        } catch (error) {
            message.error('Failed to download report. Please try again.');
            console.error('Error downloading bowser transaction report:', error);
        } finally {
            this.setState({ downloadLoading: false });
        }
    };

    handleTableChange = (pagination) => {
        this.setState({
            currentPage: pagination.current,
            pageSize: pagination.pageSize
        }, () => {
            // Re-fetch data with new pagination parameters
            const { getFieldValue } = this.props.form;
            const normalizedPeriod = this.getDayBoundaries(getFieldValue('periodFromTo')[0], getFieldValue('periodFromTo')[1]);
            const values = {
                bowserAsset: getFieldValue('bowserAsset'),
                periodFrom: normalizedPeriod.periodFrom,
                periodTo: normalizedPeriod.periodTo,
            };

            if (values.bowserAsset && values.periodFrom && values.periodTo) {
                this.generateBowserTransactionReport(values, false);
            }
        });
    };

    deleteTransaction = (id) => {
        const { getFieldValue } = this.props.form;
        const normalizedPeriod = this.getDayBoundaries(getFieldValue('periodFromTo')[0], getFieldValue('periodFromTo')[1]);
        const originalPayload = {
            bowserAsset: getFieldValue('bowserAsset'),
            periodFrom: normalizedPeriod.periodFrom,
            periodTo: normalizedPeriod.periodTo,
        };
        this.props.deleteBowserFuelTransaction(id, originalPayload);
    };

    getTableColumns = () => {
        return [
            {
                title: 'Date',
                dataIndex: 'datetime',
                key: 'datetime',
                render: (datetime) => <div>{moment(datetime).format('DD-MM-YYYY HH:mm A')}</div>
            },
            {
                title: 'Bowser Asset',
                dataIndex: 'bowser',
                key: 'bowser',
                render: (bowser) => bowser && (bowser.assetCode || '-') + '/' + (bowser.ownerCode || '-') + '/' + (bowser.altName || '-')
            },
            {
                title: 'Location',
                dataIndex: 'location',
                key: 'location',
                render: (_, record) => record.bowser && record.bowser.location && record.bowser.location.name
            },
            {
                title: 'Sub Location',
                dataIndex: 'subLocation',
                key: 'subLocation',
                render: (_, record) => record.bowser && record.bowser.subLocation && record.bowser.subLocation.name
            },
            {
                title: 'Fuel Type',
                dataIndex: 'fuelType',
                key: 'fuelType',
                render: (_, record) => record.bowser && record.bowser.assetModel && record.bowser.assetModel.fuelType && record.bowser.assetModel.fuelType.name
            },
            {
                title: 'Transaction Type',
                dataIndex: 'bowserRefuelingType',
                key: 'bowserRefuelingType',
                render: (bowserRefuelingType) => bowserRefuelingType
            },
            {
                title: 'Quantity Refilled',
                dataIndex: 'quantityAdded',
                key: 'quantityAdded',
                render: (quantityAdded) => <div>{quantityAdded} L</div>
            },
            {
                title: 'Quantity Dispensed',
                dataIndex: 'quantityRemoved',
                key: 'quantityRemoved',
                render: (quantityRemoved) => <div>{quantityRemoved} L</div>
            },
            {
                title: 'Balance',
                dataIndex: 'balance',
                key: 'balance',
                render: (balance) => <div>{Number(balance).toFixed(2) || 0} L</div>
            },
            {
                title: 'Refuel Voucher',
                dataIndex: 'refFuelRecord',
                key: 'refFuelRecord',
                render: (refFuelRecord) => refFuelRecord && refFuelRecord.voucherNo
            },
            {
                title: 'Refueled Asset',
                dataIndex: 'refAsset',
                key: 'refAsset',
                render: (refAsset) => {
                    if (refAsset) {
                        return (<div>{refAsset.assetCode || '-'}/{refAsset.ownerCode || '-'}/{refAsset.altName || '-'}</div>);
                    }
                    return 'N/A';
                }
            },
            {
                title: 'User',
                dataIndex: 'user',
                key: 'user',
                render: (user) => user && user.name
            },
            {
                title: 'Action',
                dataIndex: 'action',
                key: 'action',
                render: (_, record) => record.refFuelRecord && record.refFuelRecord.voucherNo ? null : <div>
                    <Button type="link" onClick={() => this.deleteTransaction(record._id)}>Delete</Button>
                </div>
            }
        ];
    };

    render() {
        const { getFieldDecorator } = this.props.form;
        const { loading, bowserTransactionData, bowserAssets } = this.props;
        const { currentPage, pageSize, currentTimePeriod, downloadLoading } = this.state;
        const { data, count } = bowserTransactionData;
        // console.log(currentPage, pageSize, count)
        // console.log('data', JSON.stringify(data, null, 2))

        return (
            <Spin spinning={loading}>
                <Card className={`gx-card-metrics`} title={`Bowser Transaction Report (${currentTimePeriod.charAt(0).toUpperCase() + currentTimePeriod.slice(1)})`}>
                    <Form
                        onSubmit={this.handleSubmit}
                        className="ant-advanced-search-form"
                        layout="inline"
                        labelAlign="left"
                    >
                        <Row>
                            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                                <Form.Item key={'bowserAsset'} label={'Bowser Asset'} style={{ width: '100%' }}>
                                    {getFieldDecorator(`bowserAsset`, {
                                        rules: [{ required: true, message: `Bowser Asset is required!` }],
                                    })(<Select
                                        showSearch
                                        showArrow
                                        allowClear
                                        onClear={() => this.props.form.setFieldsValue({ bowserAsset: null })}
                                        filterOption={(input, option) =>
                                            (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                        }
                                        placeholder={`Select Bowser Asset`}
                                    >
                                        {(bowserAssets || []).map(item =>
                                            <Select.Option key={item._id}
                                                value={item._id}>{item.assetCode}/ {item.ownerCode}/ {item.altName}</Select.Option>)}
                                    </Select>)
                                    }
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                                <Form.Item
                                    key={"periodFromTo"}
                                    label={"Select Period"}
                                    style={{ width: "100%" }}
                                >
                                    {getFieldDecorator(`periodFromTo`, {
                                        rules: [
                                            { required: true, message: `Period From To is required!` }
                                        ],
                                        initialValue: null
                                    })(<RangePicker style={{ width: "100%" }} />)}
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                                <Form.Item style={{ marginTop: '25px' }}>
                                    <span style={{ display: 'flex', flexDirection: 'row' }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading || downloadLoading}
                                            disabled={downloadLoading}
                                        >
                                            Generate Report
                                        </Button>
                                        <Button
                                            disabled={!bowserTransactionData || !data || data.length === 0 || downloadLoading}
                                            type="default"
                                            htmlType="button"
                                            loading={downloadLoading || loading}
                                            icon={'download'}
                                            onClick={this.downloadReport}
                                            style={{ marginLeft: '8px' }}
                                        >
                                            Download
                                        </Button>
                                    </span>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>

                    {bowserTransactionData && data && data.length > 0 ? (
                        <Table
                            dataSource={data}
                            columns={this.getTableColumns()}
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: count,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                            }}
                            onChange={this.handleTableChange}
                            scroll={{ x: true }}
                            bordered
                        />
                    ) : loading ? (
                        <Skeleton active />
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </Card>
            </Spin>
        );
    }
}

const mapStateToProps = ({ auth, fuel, asset, commonData }) => {
    return {
        authUser: auth.authUser,
        loading: commonData.loading,
        bowserAssets: asset.bowsers,
        bowserTransactionData: fuel.bowser_transaction_data,
    };
};

function mapDispatchToProps(dispatch) {
    return {
        fetchBowserAssets,
        getBowserFuelTransactions,
        deleteBowserFuelTransaction,
        downloadBowserFuelTransactions,
    };
}

const BowserTransactionReportForm = Form.create({ name: 'bowser-transaction-report' })(BowserTransactionReport);
export default connect(mapStateToProps, mapDispatchToProps())(BowserTransactionReportForm);
